import { comprimento, multiplicar, subtrair, type Vetor3 } from '../geometria/vetor3'
import type { ProporcoesVitima } from './perfisVitima'
import {
  direcoesDaVitima,
  eixoColunaDaVitima,
  type ColocacaoVitima,
  type PoseVitima,
} from './rigVitima'

export interface AlinhamentoPrancha {
  /** Ponto corporal sob o qual a prancha deve terminar a entrada. */
  base: Vetor3
  /** Eixo longitudinal atual do corpo, inclusive depois do giro de alinhamento. */
  direcaoCorpo: Vetor3
}

/**
 * Mantém a prancha sob o eixo da vítima durante toda a pá de forno.
 *
 * A vítima sobe na primeira fase, mas a prancha permanece no plano de apoio.
 * Depois, ambas compartilham o mesmo deslocamento e a mesma guinada. Usar a
 * colocação inicial aqui fazia o boneco girar enquanto a prancha permanecia no
 * rumo antigo, causando o desalinhamento visível no fim da técnica.
 */
export function alinharPranchaNaPaDeForno(
  colocacaoAtual: ColocacaoVitima,
  proporcoes: ProporcoesVitima,
  pose: PoseVitima,
  direcaoElevacao: Vetor3,
  alturaElevacao: number,
  fracaoElevada: number,
): AlinhamentoPrancha {
  const elevacao = multiplicar(direcaoElevacao, alturaElevacao * fracaoElevada)
  const colocacaoNoPlano: ColocacaoVitima = {
    ...colocacaoAtual,
    posicaoPelve: subtrair(colocacaoAtual.posicaoPelve, elevacao),
  }

  return {
    base: eixoColunaDaVitima(colocacaoNoPlano, proporcoes, pose).baseColuna,
    direcaoCorpo: direcoesDaVitima(colocacaoAtual, pose).eixo,
  }
}

/** Abaixo do que se considera uma projeção horizontal definida do eixo da coluna. */
const HORIZONTAL_MINIMA = 0.05

/**
 * Direção do tabuleiro sobre as rodas (sem pá de forno, sem elevação a calcular).
 *
 * Acompanha o eixo real do tronco assim que ele tem uma projeção horizontal
 * definida — corpo já girando ou reclinando sobre a prancha. Antes disso, com
 * o corpo ainda sentado (eixo quase vertical), usa `rumoDaRota` como
 * referência: não há "direção do corpo" ainda para seguir.
 *
 * Antes desta função a prancha usava sempre `rumoDaRota`, fixo do início ao
 * fim. Isso só bate com a direção final do tronco quando o corpo gira em
 * bloco (`fracaoTorcao: 0` em `amostrarRota`, `girarEmBloco` no App). Com a
 * distribuição padrão de torção residual (`FRACAO_TORCAO_PADRAO`,
 * `girarEmBloco` desligado — o padrão do app), o tronco não gira o giro
 * inteiro, e a prancha ficava até ~20° fora do eixo real do corpo na posição
 * final. Achado da revisão visual da Fase 1.
 */
export function direcaoPranchaSobreRodas(
  colocacaoAtual: ColocacaoVitima,
  pose: PoseVitima,
  rumoDaRota: Vetor3,
): Vetor3 {
  const eixoDoTronco = direcoesDaVitima(colocacaoAtual, pose).eixo
  const horizontalDoTronco: Vetor3 = { x: eixoDoTronco.x, y: 0, z: eixoDoTronco.z }
  return comprimento(horizontalDoTronco) > HORIZONTAL_MINIMA ? horizontalDoTronco : rumoDaRota
}

/** Mantém o ponto de apoio e o eixo da prancha sob a vítima durante a extração sobre rodas. */
export function alinharPranchaSobreRodas(
  colocacaoAtual: ColocacaoVitima,
  proporcoes: ProporcoesVitima,
  pose: PoseVitima,
  rumoDaRota: Vetor3,
): AlinhamentoPrancha {
  return {
    base: eixoColunaDaVitima(colocacaoAtual, proporcoes, pose).baseColuna,
    direcaoCorpo: direcaoPranchaSobreRodas(colocacaoAtual, pose, rumoDaRota),
  }
}
